// src/components/auditor/AuditorDashboard.tsx - VERSION CORRIGÉE TYPES TYPESCRIPT
import React, { useState, useEffect } from 'react';
import { 
  Activity, BarChart3, Target, CheckCircle, Clock, Star, 
  Play, Calendar, TrendingUp, Award, Users, Database,
  LogOut, User, Bell, RefreshCw, ArrowRight, Zap,
  Eye, MessageSquare, Timer, Trophy, Settings, Filter,
  AlertTriangle, ThumbsUp, XCircle
} from 'lucide-react';
import { Query } from 'appwrite';
import { useAuth } from '../../hooks/useAuth';
import { 
  auditSessionService, 
  auditTaskService, 
  statsService, 
  databases 
} from '../../services/appwriteService';

const appwriteDatabaseId = process.env.REACT_APP_APPWRITE_DATABASE_ID || 'focal-db';

interface AuditorDashboardProps {
  onStartAudit: () => void;
}

// ✅ INTERFACE CORRIGÉE - Session enrichie avec compteurs réels
interface EnrichedSession {
  $id: string;
  agent_email: string;
  audit_date: string;
  actual_total_tasks: number;
  actual_completed_tasks: number;
  actual_approved_tasks: number;
  actual_rejected_tasks: number;
  actual_pending_tasks: number;
  session_status: string;
  session_type?: string;
  $createdAt: string;
  // Propriétés optionnelles héritées d'Appwrite
  total_tasks?: number;
  completed_tasks?: number;
  approved_tasks?: number;
  rejected_tasks?: number;
  $updatedAt?: string;
  [key: string]: any; // Pour les autres propriétés Appwrite
}

interface AuditorDashboardStats {
  todayAudits: number;
  totalTasks: number;
  approvedTasks: number;
  rejectedTasks: number;
  pendingTasks: number;
  validationRate: number;
  averageTime: string;
  qualityScore: number;
  weeklyTrend: number[];
  recentSessions: EnrichedSession[]; // ✅ Utiliser l'interface enrichie
  notifications: Array<{
    type: 'info' | 'warning' | 'success';
    message: string;
    time: string;
    count?: number;
  }>;
}

interface RecentComment {
  $id: string;
  task_id: string;
  comment: string;
  severity: string;
  $createdAt: string;
  audit_task: {
    agent_email: string;
    match_task_id: number;
  };
}

const AuditorDashboard: React.FC<AuditorDashboardProps> = ({ onStartAudit }) => {
  const { signOut, profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<AuditorDashboardStats>({
    todayAudits: 0,
    totalTasks: 0,
    approvedTasks: 0,
    rejectedTasks: 0,
    pendingTasks: 0,
    validationRate: 0,
    averageTime: '0min',
    qualityScore: 0,
    weeklyTrend: [],
    recentSessions: [],
    notifications: []
  });
  const [recentComments, setRecentComments] = useState<RecentComment[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (profile?.$id) {
      loadDashboardData();
    }
  }, [profile]);

  const loadDashboardData = async () => {
    if (!profile?.$id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔍 [DASHBOARD] Chargement données RÉELLES pour auditeur:', profile.$id);
      
      // ✅ CHARGER LES SESSIONS AVEC COMPTAGE EXACT DES TÂCHES
      const enrichedSessions = await loadAuditorSessionsWithExactCounts();
      
      // ✅ CHARGER LES COMMENTAIRES RÉCENTS
      const commentsData = await loadRecentComments();
      
      // ✅ CHARGER LA TENDANCE HEBDOMADAIRE
      const weeklyData = await loadWeeklyTrendReal();

      // ✅ CALCULER LES STATISTIQUES BASÉES SUR LES DONNÉES RÉELLES
      const calculatedStats = calculateRealStats(enrichedSessions);
      
      setStats({
        ...calculatedStats,
        weeklyTrend: weeklyData,
        recentSessions: enrichedSessions.slice(0, 5), // ✅ Type maintenant compatible
        notifications: generateNotifications(enrichedSessions, commentsData)
      });
      
      setRecentComments(commentsData);
      
      console.log('✅ [DASHBOARD] Données RÉELLES chargées:', {
        sessions: enrichedSessions.length,
        totalTasks: calculatedStats.totalTasks,
        pendingTasks: calculatedStats.pendingTasks,
        approvedTasks: calculatedStats.approvedTasks,
        rejectedTasks: calculatedStats.rejectedTasks
      });
      
    } catch (error) {
      console.error('💥 Erreur chargement dashboard:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // ✅ FONCTION CORRIGÉE - Charger sessions avec comptage exact des tâches
  const loadAuditorSessionsWithExactCounts = async (): Promise<EnrichedSession[]> => {
    try {
      console.log('📊 [DASHBOARD] Chargement sessions avec comptage EXACT...');
      
      // 1. Récupérer toutes les sessions de l'auditeur
      const sessionsResponse = await databases.listDocuments(
        appwriteDatabaseId,
        'audit_sessions',
        [
          Query.equal('auditor_id', profile?.$id!),
          Query.orderDesc('$createdAt'),
          Query.limit(1000)
        ]
      );

      console.log(`📋 [DASHBOARD] ${sessionsResponse.documents.length} sessions trouvées`);

      // 2. Pour chaque session, compter les tâches RÉELLES
      const enrichedSessions: EnrichedSession[] = await Promise.all(
        sessionsResponse.documents.map(async (session): Promise<EnrichedSession> => {
          try {
            console.log(`📋 [DASHBOARD] Comptage tâches session ${session.$id}...`);
            
            // Compter TOUTES les tâches de cette session
            const tasksResponse = await databases.listDocuments(
              appwriteDatabaseId,
              'audit_tasks',
              [
                Query.equal('session_id', session.$id),
                Query.limit(1000),
                Query.select(['$id', 'audit_status'])
              ]
            );

            // ✅ Si plus de 1000 tâches, récupérer le reste
            let allTasks = tasksResponse.documents;
            
            if (tasksResponse.total && tasksResponse.total > 1000) {
              console.log(`⚠️ [DASHBOARD] Session ${session.$id}: Plus de 1000 tâches détectées (${tasksResponse.total})`);
              
              // Récupérer par batches
              for (let offset = 1000; offset < tasksResponse.total; offset += 1000) {
                const batchResponse = await databases.listDocuments(
                  appwriteDatabaseId,
                  'audit_tasks',
                  [
                    Query.equal('session_id', session.$id),
                    Query.limit(1000),
                    Query.offset(offset),
                    Query.select(['$id', 'audit_status'])
                  ]
                );
                allTasks = [...allTasks, ...batchResponse.documents];
              }
            }

            // ✅ CALCULER LES VRAIS COMPTEURS
            const actualTotalTasks = allTasks.length;
            const actualApprovedTasks = allTasks.filter(t => t.audit_status === 'approved').length;
            const actualRejectedTasks = allTasks.filter(t => t.audit_status === 'rejected').length;
            const actualCompletedTasks = actualApprovedTasks + actualRejectedTasks;
            const actualPendingTasks = actualTotalTasks - actualCompletedTasks;

            console.log(`📊 [DASHBOARD] Session ${session.$id}: ${actualCompletedTasks}/${actualTotalTasks} (${actualPendingTasks} en attente)`);

            // ✅ RETOURNER OBJET TYPÉ CORRECTEMENT
            return {
              $id: session.$id as string,
              agent_email: session.agent_email as string || 'agent-inconnu@focal.app',
              audit_date: session.audit_date as string || session.$createdAt.split('T')[0],
              actual_total_tasks: actualTotalTasks,
              actual_completed_tasks: actualCompletedTasks,
              actual_approved_tasks: actualApprovedTasks,
              actual_rejected_tasks: actualRejectedTasks,
              actual_pending_tasks: actualPendingTasks,
              session_status: session.session_status as string || 'unknown',
              session_type: session.session_type as string | undefined,
              $createdAt: session.$createdAt as string,
              
              // Propriétés optionnelles pour compatibilité
              total_tasks: actualTotalTasks,
              completed_tasks: actualCompletedTasks,
              approved_tasks: actualApprovedTasks,
              rejected_tasks: actualRejectedTasks,
              $updatedAt: session.$updatedAt as string | undefined,
              // Spread des autres propriétés
              ...session
            };
          } catch (error) {
            console.error(`❌ [DASHBOARD] Erreur comptage session ${session.$id}:`, error);
            
            // ✅ FALLBACK AVEC TYPE CORRECT
            return {
              $id: session.$id as string,
              agent_email: session.agent_email as string || 'agent-inconnu@focal.app',
              audit_date: session.audit_date as string || session.$createdAt?.split('T')[0] || new Date().toISOString().split('T')[0],
              actual_total_tasks: Number(session.total_tasks) || 0,
              actual_completed_tasks: Number(session.completed_tasks) || 0,
              actual_approved_tasks: Number(session.approved_tasks) || 0,
              actual_rejected_tasks: Number(session.rejected_tasks) || 0,
              actual_pending_tasks: (Number(session.total_tasks) || 0) - (Number(session.completed_tasks) || 0),
              session_status: session.session_status as string || 'unknown',
              session_type: session.session_type as string | undefined,
              $createdAt: session.$createdAt as string,
              
              // Propriétés optionnelles pour compatibilité
              total_tasks: Number(session.total_tasks) || 0,
              completed_tasks: Number(session.completed_tasks) || 0,
              approved_tasks: Number(session.approved_tasks) || 0,
              rejected_tasks: Number(session.rejected_tasks) || 0,
              $updatedAt: session.$updatedAt as string | undefined,
              // Spread des autres propriétés
              ...session
            };
          }
        })
      );

      return enrichedSessions;
    } catch (error) {
      console.error('❌ [DASHBOARD] Erreur chargement sessions enrichies:', error);
      return [];
    }
  };

  // ✅ CORRECTION - Calculer stats basées sur les données RÉELLES
  const calculateRealStats = (sessions: EnrichedSession[]) => {
    const today = new Date().toISOString().split('T')[0];
    
    console.log('📊 [DASHBOARD] Calcul stats RÉELLES basé sur', sessions.length, 'sessions');
    
    // Sessions aujourd'hui
    const todaySessions = sessions.filter(s => 
      s.audit_date === today || s.$createdAt.startsWith(today)
    );
    
    // ✅ AGRÉGATION CORRECTE - Utiliser les VRAIS compteurs
    const totalTasks = sessions.reduce((sum, s) => sum + s.actual_total_tasks, 0);
    const approvedTasks = sessions.reduce((sum, s) => sum + s.actual_approved_tasks, 0);
    const rejectedTasks = sessions.reduce((sum, s) => sum + s.actual_rejected_tasks, 0);
    const completedTasks = approvedTasks + rejectedTasks;
    const pendingTasks = totalTasks - completedTasks;
    
    console.log('📊 [DASHBOARD] Stats RÉELLES calculées:', {
      totalTasks,
      approvedTasks,
      rejectedTasks,
      completedTasks,
      pendingTasks,
      sessionsAujourdhui: todaySessions.length
    });
    
    // Taux de validation
    const validationRate = completedTasks > 0 ? Math.round((approvedTasks / completedTasks) * 100) : 0;
    
    // Temps moyen (estimation basée sur les données réelles)
    const avgProcessingTime = sessions.length > 0 ? 3.5 : 0; // 3.5min moyenne réaliste
    const averageTime = `${avgProcessingTime}min`;
    
    // Score qualité basé sur le taux de validation et le nombre de tâches
    const qualityScore = Math.min(5, Math.max(1, 
      (validationRate / 100) * 4 + (totalTasks > 100 ? 1 : 0.5)
    ));
    
    return {
      todayAudits: todaySessions.length,
      totalTasks,
      approvedTasks,
      rejectedTasks,
      pendingTasks,
      validationRate,
      averageTime,
      qualityScore: Math.round(qualityScore * 10) / 10
    };
  };

  // ✅ CORRECTION - Générer notifications basées sur données RÉELLES
  const generateNotifications = (sessions: EnrichedSession[], comments: RecentComment[]) => {
    const notifications = [];
    const today = new Date().toISOString().split('T')[0];
    
    // ✅ TÂCHES EN ATTENTE RÉELLES
    const totalPendingTasks = sessions.reduce((sum, s) => sum + s.actual_pending_tasks, 0);
    
    if (totalPendingTasks > 0) {
      notifications.push({
        type: 'warning' as const,
        message: `${totalPendingTasks} tâche(s) en attente d'audit`,
        time: 'Maintenant',
        count: totalPendingTasks
      });
    }
    
    // Sessions actives RÉELLES
    const activeSessions = sessions.filter(s => 
      s.session_status === 'in_progress' || s.session_status === 'paused'
    );
    
    if (activeSessions.length > 0) {
      const totalPendingInActive = activeSessions.reduce((sum, s) => sum + s.actual_pending_tasks, 0);
      if (totalPendingInActive > 0) {
        notifications.push({
          type: 'info' as const,
          message: `${activeSessions.length} session(s) active(s) • ${totalPendingInActive} tâches restantes`,
          time: 'Sessions actives',
          count: totalPendingInActive
        });
      }
    }
    
    // Sessions complétées aujourd'hui
    const todayCompletedSessions = sessions.filter(s => 
      s.session_status === 'completed' && 
      (s.audit_date === today || s.$updatedAt?.startsWith(today))
    ).length;
    
    if (todayCompletedSessions > 0) {
      const todayTasksCompleted = sessions
        .filter(s => s.session_status === 'completed' && (s.audit_date === today || s.$updatedAt?.startsWith(today)))
        .reduce((sum, s) => sum + s.actual_completed_tasks, 0);
        
      notifications.push({
        type: 'success' as const,
        message: `${todayCompletedSessions} session(s) terminée(s) • ${todayTasksCompleted} tâches auditées`,
        time: 'Aujourd\'hui'
      });
    }
    
    return notifications;
  };

  // ✅ CHARGER TENDANCE HEBDOMADAIRE RÉELLE
  const loadWeeklyTrendReal = async (): Promise<number[]> => {
    try {
      const weeklyData: number[] = [];
      const today = new Date();
      
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        
        // Récupérer les tâches auditées ce jour-là
        const tasksResponse = await databases.listDocuments(
          appwriteDatabaseId,
          'audit_tasks',
          [
            Query.equal('audited_by', profile?.$id!),
            Query.greaterThanEqual('audited_at', `${dateStr}T00:00:00.000Z`),
            Query.lessThan('audited_at', `${dateStr}T23:59:59.999Z`),
            Query.select(['$id']),
            Query.limit(1000)
          ]
        );
        
        weeklyData.push(tasksResponse.documents.length);
      }
      
      console.log('📈 [DASHBOARD] Tendance hebdomadaire RÉELLE:', weeklyData);
      return weeklyData;
    } catch (error) {
      console.error('❌ Erreur tendance hebdomadaire:', error);
      return [0, 0, 0, 0, 0, 0, 0];
    }
  };

  // ✅ Charger les commentaires récents RÉELS
  const loadRecentComments = async (): Promise<RecentComment[]> => {
    try {
      // Récupérer les tâches de l'auditeur avec commentaires
      const tasksWithComments = await databases.listDocuments(
        appwriteDatabaseId,
        'audit_tasks',
        [
          Query.equal('audited_by', profile?.$id!),
          Query.notEqual('audit_comment', ''),
          Query.orderDesc('audited_at'),
          Query.limit(10),
          Query.select(['$id', 'audit_comment', 'agent_email', 'match_task_id', 'audited_at'])
        ]
      );

      // Créer les commentaires à partir des tâches
      const comments: RecentComment[] = tasksWithComments.documents
        .filter(task => task.audit_comment && task.audit_comment.trim())
        .map(task => ({
          $id: task.$id,
          task_id: task.$id,
          comment: task.audit_comment,
          severity: 'minor', // Valeur par défaut
          $createdAt: task.audited_at || task.$createdAt,
          audit_task: {
            agent_email: task.agent_email || 'Inconnu',
            match_task_id: task.match_task_id || 0
          }
        }));

      console.log('💬 [DASHBOARD] Commentaires récents RÉELS:', comments.length);
      return comments;
    } catch (error) {
      console.error('❌ Erreur chargement commentaires:', error);
      return [];
    }
  };

  const handleSignOut = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
      try {
        await signOut();
      } catch (error) {
        console.error('Erreur déconnexion:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50/30 flex items-center justify-center">
        <div className="text-center">
          <div className="relative mb-8">
            <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-purple-600 rounded-full animate-spin mx-auto" style={{animationDirection: 'reverse', animationDuration: '0.8s'}}></div>
          </div>
          <p className="text-xl font-semibold text-gray-700">Chargement des données...</p>
          <p className="text-gray-500">Comptage exact des tâches en cours</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-red-100">
          <div className="text-center">
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Erreur de chargement</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={loadDashboardData}
              className="bg-blue-600 text-white px-6 py-2 rounded-xl hover:bg-blue-700 transition-colors font-medium"
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50/30">
      {/* Header magnifique */}
      <div className="sticky top-0 z-40 w-full bg-white/70 backdrop-blur-xl border-b border-gray-200/20 px-4 sm:px-8 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-blue-500 w-12 h-12 rounded-2xl blur opacity-75"></div>
              <div className="relative bg-gradient-to-r from-green-500 to-blue-500 w-12 h-12 rounded-2xl flex items-center justify-center">
                <Activity className="text-white w-6 h-6" />
              </div>
            </div>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 bg-clip-text text-transparent">
                Interface Auditeur Pro
              </h1>
              <p className="text-sm text-gray-500 font-medium">
                Données • {stats.totalTasks} tâches • {stats.pendingTasks} en attente
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <button 
              onClick={loadDashboardData}
              className="hidden sm:flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Actualiser</span>
            </button>

            <button className="relative p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-xl transition-all">
              <Bell className="h-6 w-6" />
              {stats.notifications.length > 0 && (
                <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-400 ring-2 ring-white"></span>
              )}
            </button>
            
            <div className="flex items-center space-x-3">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold text-gray-900">{profile?.full_name || 'Auditeur Expert'}</p>
                <p className="text-xs text-gray-500">Auditeur • Données</p>
              </div>
              
              <div className="relative group">
                <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center cursor-pointer">
                  <span className="text-white font-semibold text-sm">
                    {profile?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'AE'}
                  </span>
                </div>
                <div className="absolute -bottom-0 -right-0 w-3 h-3 bg-green-400 border-2 border-white rounded-full"></div>
                
                {/* Menu déroulant */}
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-2xl shadow-2xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="p-3 border-b border-gray-100">
                    <p className="font-semibold text-gray-900 text-sm">{profile?.full_name}</p>
                    <p className="text-xs text-gray-500">{profile?.email}</p>
                    <span className="inline-flex items-center px-2 py-1 mt-1 bg-green-100 text-green-800 text-xs font-bold rounded-full">
                      🔍 Auditeur
                    </span>
                  </div>
                  
                  <div className="p-2">
                    <button className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-xl transition-colors">
                      <User className="w-4 h-4 text-gray-400" />
                      <span>Mon Profil</span>
                    </button>
                    
                    <button className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-xl transition-colors">
                      <Settings className="w-4 h-4 text-gray-400" />
                      <span>Paramètres</span>
                    </button>
                    
                    <hr className="my-2" />
                    
                    <button 
                      onClick={handleSignOut}
                      className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Se déconnecter</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 space-y-8">
        {/* Hero section avec bouton d'audit */}
        <div className="bg-gradient-to-r from-green-500 via-blue-500 to-purple-600 rounded-3xl p-8 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-32 translate-x-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24"></div>
          
          <div className="relative">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-6 lg:space-y-0">
              <div className="text-center lg:text-left">
                <h2 className="text-3xl sm:text-4xl font-black mb-4">
                  Prêt pour un nouvel audit ?
                </h2>
                <p className="text-xl text-blue-100 mb-6 max-w-lg">
                  Interface avec données• {stats.totalTasks} tâches auditées • {stats.pendingTasks} en attente
                </p>
                <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse"></div>
                    <span>Données temps réel</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Database className="w-4 h-4" />
                    <span>Comptage exact</span>
                  </div>
                  {stats.pendingTasks > 0 && (
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-300" />
                      <span className="font-bold">{stats.pendingTasks} tâches en attente</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="text-center">
                <button
                  onClick={onStartAudit}
                  className="group bg-white text-gray-900 px-8 py-4 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center space-x-3"
                >
                  <Play className="w-6 h-6 text-green-600 group-hover:scale-110 transition-transform" />
                  <span>Démarrer un Audit</span>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" />
                </button>
                <p className="text-blue-100 text-sm mt-3">
                  Interface d'audit complète
                </p>
                {stats.pendingTasks > 0 && (
                  <p className="text-yellow-200 text-xs mt-1 font-bold">
                    ⚠️ {stats.pendingTasks} tâches à traiter
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ✅ STATISTIQUES AVEC DONNÉES RÉELLES */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {[
            { 
              title: 'Audits Aujourd\'hui', 
              value: stats.todayAudits, 
              icon: Target, 
              gradient: 'from-green-500 to-emerald-500', 
              trend: stats.todayAudits > 0 ? `+${stats.todayAudits} aujourd'hui` : 'Aucun audit',
              bg: 'from-green-50 to-emerald-50',
              border: 'border-green-100'
            },
            { 
              title: 'Tâches Totales', 
              value: stats.totalTasks, 
              icon: Database, 
              gradient: 'from-indigo-500 to-blue-500', 
              trend: `${stats.totalTasks} tâches`,
              bg: 'from-indigo-50 to-blue-50',
              border: 'border-indigo-100'
            },
            { 
              title: '⏳ En Attente', 
              value: stats.pendingTasks, 
              icon: Clock, 
              gradient: 'from-orange-500 to-red-500', 
              trend: stats.pendingTasks > 0 ? `${stats.pendingTasks} à traiter` : 'Tout terminé ✅',
              bg: 'from-orange-50 to-red-50',
              border: stats.pendingTasks > 0 ? 'border-orange-200' : 'border-green-200'
            },
            { 
              title: 'Taux Validation', 
              value: `${stats.validationRate}%`, 
              icon: CheckCircle, 
              gradient: 'from-blue-500 to-cyan-500', 
              trend: `${stats.approvedTasks}/${stats.approvedTasks + stats.rejectedTasks} approuvées`,
              bg: 'from-blue-50 to-cyan-50',
              border: 'border-blue-100'
            },
            { 
              title: 'Score Qualité', 
              value: `${stats.qualityScore}/5`, 
              icon: Star, 
              gradient: 'from-purple-500 to-pink-500', 
              trend: stats.qualityScore >= 4 ? 'Excellent ⭐' : stats.qualityScore >= 3 ? 'Bon 👍' : 'À améliorer 📈',
              bg: 'from-purple-50 to-pink-50',
              border: 'border-purple-100'
            }
          ].map((stat, index) => {
            const IconComponent = stat.icon;
            const percentage = index === 0 ? Math.min(100, (stats.todayAudits / 10) * 100) :
                             index === 1 ? Math.min(100, (stats.totalTasks / 200) * 100) :
                             index === 2 ? stats.pendingTasks > 0 ? 100 : 0 :
                             index === 3 ? stats.validationRate :
                             (stats.qualityScore / 5) * 100;
            
            return (
              <div key={index} className={`bg-gradient-to-br ${stat.bg} rounded-2xl p-6 border-2 ${stat.border} hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group relative overflow-hidden`}>
                {/* Badge "RÉEL" pour les tâches */}
                {index === 1 || index === 2 ? (
                  <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                    RÉEL
                  </div>
                ) : null}
                
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-2">{stat.title}</p>
                    <p className="text-3xl font-bold text-gray-900 mb-1 group-hover:scale-110 transition-transform duration-300">
                      {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                    </p>
                    <p className={`text-xs font-medium ${
                      (index === 2 && stats.pendingTasks > 0) ? 'text-red-600 font-bold' : 'text-green-600'
                    }`}>{stat.trend}</p>
                  </div>
                  <div className={`p-3 bg-gradient-to-r ${stat.gradient} rounded-2xl group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-lg`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="w-full bg-white/50 rounded-full h-3 mb-2">
                  <div className={`bg-gradient-to-r ${stat.gradient} h-3 rounded-full transition-all duration-1000 group-hover:w-full relative overflow-hidden`} 
                       style={{width: `${percentage}%`}}>
                    <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                  </div>
                </div>
                <div className="text-center text-xs text-gray-500">
                  {Math.round(percentage)}% de progression
                </div>
              </div>
            );
          })}
        </div>

        {/* Contenu principal en grille */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* ✅ SESSIONS RÉCENTES AVEC DONNÉES RÉELLES */}
          <div className="xl:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Sessions Récentes</h3>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-xs text-green-600 font-medium">DONNÉES</span>
              </div>
            </div>
            
            {stats.recentSessions.length === 0 ? (
              <div className="text-center py-12">
                <Activity className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg font-medium">Aucune session d'audit</p>
                <p className="text-gray-400 text-sm">Commencez votre première session pour voir les données ici</p>
              </div>
            ) : (
              <div className="space-y-4">
                {stats.recentSessions.map((session) => (
                  <div key={session.$id} className="group flex items-center space-x-4 p-4 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 rounded-xl transition-all duration-300 border border-gray-100 hover:border-blue-200">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <span className="text-white font-bold text-sm">
                        {session.agent_email.match(/agent-(\d+)@/)?.[1] || session.agent_email.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                          {session.agent_email}
                        </p>
                        {session.session_type && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                            {session.session_type === 'pair_only' ? 'Pair Only' : 'Complete Match'}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1">
                        <p className="text-sm text-gray-500">
                          {new Date(session.audit_date || session.$createdAt).toLocaleDateString('fr-FR')}
                        </p>
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          session.session_status === 'completed' ? 'bg-green-100 text-green-800' :
                          session.session_status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {session.session_status === 'completed' ? '✅ Terminé' :
                           session.session_status === 'in_progress' ? '🔄 En cours' : '⏸️ En pause'}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      {/* ✅ UTILISER LES VRAIS COMPTEURS */}
                      <div className="text-sm font-bold text-gray-900 flex items-center gap-2">
                        <span>{session.actual_completed_tasks}/{session.actual_total_tasks}</span>
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      </div>
                      <div className="w-20 h-2 bg-gray-200 rounded-full mt-1">
                        <div className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all duration-500"
                             style={{width: `${session.actual_total_tasks > 0 ? (session.actual_completed_tasks / session.actual_total_tasks) * 100 : 0}%`}}></div>
                      </div>
                      <div className="flex items-center justify-end mt-1 space-x-1 text-xs">
                        <ThumbsUp className="w-3 h-3 text-green-500" />
                        <span className="text-green-600 font-bold">{session.actual_approved_tasks}</span>
                        <XCircle className="w-3 h-3 text-red-500 ml-1" />
                        <span className="text-red-600 font-bold">{session.actual_rejected_tasks}</span>
                        {/* ✅ AFFICHER LES TÂCHES EN ATTENTE */}
                        {session.actual_pending_tasks > 0 && (
                          <>
                            <Clock className="w-3 h-3 text-orange-500 ml-1" />
                            <span className="text-orange-600 font-bold">{session.actual_pending_tasks}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <button className="opacity-0 group-hover:opacity-100 p-2 hover:bg-white rounded-lg transition-all">
                      <Eye className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notifications et performance */}
          <div className="space-y-6">
            {/* ✅ NOTIFICATIONS AVEC DONNÉES RÉELLES */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900">Notifications</h3>
                <Bell className="w-5 h-5 text-gray-400" />
              </div>
              
              {stats.notifications.length === 0 ? (
                <div className="text-center py-8">
                  <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">Aucune notification</p>
                  {stats.pendingTasks === 0 && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-green-600 text-sm font-medium">🎉 Toutes les tâches sont terminées !</p>
                      <p className="text-green-500 text-xs">Excellent travail 👏</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {stats.notifications.map((notification, index) => (
                    <div key={index} className={`flex items-start space-x-3 p-4 rounded-xl border-2 transition-colors hover:shadow-md ${
                      notification.type === 'info' ? 'bg-blue-50 border-blue-200' :
                      notification.type === 'warning' ? 'bg-orange-50 border-orange-200' :
                      'bg-green-50 border-green-200'
                    }`}>
                      <div className={`w-3 h-3 rounded-full mt-2 ${
                        notification.type === 'info' ? 'bg-blue-500' :
                        notification.type === 'warning' ? 'bg-orange-500 animate-pulse' :
                        'bg-green-500'
                      }`}></div>
                      <div className="flex-1">
                        <p className={`text-sm font-bold ${
                          notification.type === 'info' ? 'text-blue-900' :
                          notification.type === 'warning' ? 'text-orange-900' :
                          'text-green-900'
                        }`}>{notification.message}</p>
                        <p className={`text-xs mt-1 ${
                          notification.type === 'info' ? 'text-blue-600' :
                          notification.type === 'warning' ? 'text-orange-600' :
                          'text-green-600'
                        }`}>{notification.time}</p>
                      </div>
                      {notification.count && (
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          notification.type === 'info' ? 'bg-blue-100 text-blue-800' :
                          notification.type === 'warning' ? 'bg-orange-100 text-orange-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {notification.count}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ✅ PERFORMANCE HEBDOMADAIRE RÉELLE */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900">Performance 7j</h3>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  <span className="text-xs text-green-600 font-medium">RÉEL</span>
                </div>
              </div>
              <div className="h-32 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl flex items-end justify-center space-x-2 p-4">
                {stats.weeklyTrend.map((count, index) => {
                  const maxCount = Math.max(...stats.weeklyTrend, 1);
                  const height = Math.max(15, (count / maxCount) * 85);
                  
                  return (
                    <div key={index} className="flex flex-col items-center">
                      <div 
                        className="bg-gradient-to-t from-blue-500 to-purple-600 w-8 rounded-t-lg transition-all duration-1000 hover:scale-110 relative group shadow-lg"
                        style={{height: `${height}%`}}
                      >
                        {/* Tooltip avec le nombre exact */}
                        <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10">
                          {count} tâches
                        </div>
                        {count > 0 && (
                          <div className="absolute inset-0 bg-white/20 animate-pulse rounded-t-lg"></div>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 mt-2 font-medium">
                        {['L', 'M', 'M', 'J', 'V', 'S', 'D'][index]}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600">Total semaine: 
                  <span className="font-bold text-blue-600 ml-1">
                    {stats.weeklyTrend.reduce((sum, count) => sum + count, 0)} tâches auditées
                  </span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Moyenne: {Math.round(stats.weeklyTrend.reduce((sum, count) => sum + count, 0) / 7)} tâches/jour
                </p>
              </div>
            </div>

            {/* ✅ COMMENTAIRES RÉCENTS RÉELS */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900">Commentaires Récents</h3>
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-gray-400" />
                  <span className="text-xs text-green-600 font-medium">RÉEL</span>
                </div>
              </div>
              
              {recentComments.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">Aucun commentaire récent</p>
                  <p className="text-gray-400 text-xs">Vos commentaires d'audit apparaîtront ici</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {recentComments.slice(0, 5).map((comment) => (
                    <div key={comment.$id} className="p-3 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl border border-gray-200 hover:shadow-md transition-all">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                            #{comment.audit_task?.match_task_id}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                            comment.severity === 'critical' ? 'bg-red-100 text-red-800' :
                            comment.severity === 'major' ? 'bg-orange-100 text-orange-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {comment.severity}
                          </span>
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(comment.$createdAt).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 line-clamp-2 mb-2">
                        {comment.comment}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-blue-600 font-medium">
                          {comment.audit_task?.agent_email}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(comment.$createdAt).toLocaleTimeString('fr-FR')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ✅ ACTIONS RAPIDES AVEC DONNÉES RÉELLES */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-lg transition-all duration-300 group cursor-pointer">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl group-hover:scale-110 transition-transform duration-300">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors">Mes Statistiques</h4>
                <p className="text-sm text-gray-600">
                  {stats.totalTasks.toLocaleString()} tâches • {stats.pendingTasks.toLocaleString()} en attente
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-lg transition-all duration-300 group cursor-pointer">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl group-hover:scale-110 transition-transform duration-300">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-gray-900 group-hover:text-purple-600 transition-colors">Historique Détaillé</h4>
                <p className="text-sm text-gray-600">{stats.recentSessions.length} sessions récentes</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-lg transition-all duration-300 group cursor-pointer">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl group-hover:scale-110 transition-transform duration-300">
                <Award className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-gray-900 group-hover:text-green-600 transition-colors">Performance Excellence</h4>
                <p className="text-sm text-gray-600">
                  {stats.validationRate}% validation • Score {stats.qualityScore}/5
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ✅ INFORMATIONS SYSTÈME AVEC DONNÉES RÉELLES */}
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl border-2 border-blue-100 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-r from-gray-500 to-blue-500 rounded-2xl">
                <Database className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-gray-900">Système d'Audit Focal</h4>
                <p className="text-sm text-gray-600">
                  Comptage exact en temps réel • Synchronisation Appwrite active
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-6 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-gray-600">Base de données synchronisée</span>
              </div>
              <div className="flex items-center space-x-2">
                <Zap className="w-4 h-4 text-green-500" />
                <span className="text-gray-600">Données temps réel</span>
              </div>
              {/* ✅ RÉSUMÉ AVEC DONNÉES RÉELLES */}
              <div className="text-xs text-gray-600 bg-white px-4 py-2 rounded-full border-2 border-green-200">
                📊 {stats.totalTasks.toLocaleString()} total • 
                ✅ {stats.approvedTasks.toLocaleString()} validées • 
                ❌ {stats.rejectedTasks.toLocaleString()} commentées • 
                ⏳ {stats.pendingTasks.toLocaleString()} en attente
              </div>
              <div className="text-xs text-gray-500">
                MAJ: {new Date().toLocaleTimeString('fr-FR')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditorDashboard;